/* Phase 3B — Batch 6 (P3-6 Oversight) mock/domain data.
   Sources: prototype/pages/{governance,evidence,reconciliation,monitoring}/*.html
   plus existing data/mock.ts (wallets, transfers, approvals). No external data. */

export type OversightTone = "success" | "warning" | "danger" | "info" | "pending" | "neutral";

/* ───────────────────────── Governance (GOV-00/01/03) ───────────────────────── */

export type PolicyCategory = "Velocity" | "Address Allowlist" | "Quorum" | "Screening" | "Contract Allowlist";
export type PolicyStatus = "active" | "draft";

export interface PolicyCondition {
  field: string;
  operator: string;
  value: string;
}

export interface Policy {
  id: string;
  name: string;
  category: PolicyCategory;
  status: PolicyStatus;
  statusLabel: string;
  statusTone: OversightTone;
  wallets: number;
  walletIds: string[];
  lastEvaluated: string;
  type: string;
  created: string;
  modified: string;
  conditions: PolicyCondition[];
  description: string;
}

export const POLICIES: Policy[] = [
  {
    id: "POL-001",
    name: "High-value transfer velocity limit",
    category: "Velocity",
    status: "active",
    statusLabel: "Active",
    statusTone: "success",
    wallets: 3,
    walletIds: ["WLT-001", "WLT-003", "WLT-004"],
    lastEvaluated: "2026-09-15 10:44 UTC",
    type: "Velocity",
    created: "2026-06-02",
    modified: "2026-08-19 14:02 UTC",
    description: "Blocks any wallet from sending more than $500,000 equivalent within a rolling 24-hour window.",
    conditions: [
      { field: "Window", operator: "=", value: "Rolling 24 hours" },
      { field: "Amount (USD equivalent)", operator: ">", value: "$500,000" },
      { field: "Action", operator: "=", value: "Block + notify Compliance" },
    ],
  },
  {
    id: "POL-002",
    name: "Approved recipient list — external addresses",
    category: "Address Allowlist",
    status: "active",
    statusLabel: "Active",
    statusTone: "success",
    wallets: 4,
    walletIds: ["WLT-001", "WLT-003", "WLT-004", "WLT-005"],
    lastEvaluated: "2026-09-15 10:42 UTC",
    type: "Address Allowlist",
    created: "2026-05-11",
    modified: "2026-07-30 09:18 UTC",
    description: "First-time external destinations above $10,000 require the address to exist on the approved recipient list.",
    conditions: [
      { field: "Destination", operator: "not in", value: "Approved recipient list" },
      { field: "Amount (USD equivalent)", operator: ">", value: "$10,000" },
      { field: "Action", operator: "=", value: "Require Compliance pre-approval" },
    ],
  },
  {
    id: "POL-003",
    name: "Quorum approval for treasury movements",
    category: "Quorum",
    status: "active",
    statusLabel: "Active",
    statusTone: "success",
    wallets: 2,
    walletIds: ["WLT-002", "WLT-003"],
    lastEvaluated: "2026-09-15 10:40 UTC",
    type: "Quorum",
    created: "2026-04-28",
    modified: "2026-09-01 11:40 UTC",
    description: "Treasury wallet movements above $50,000 require a 2-of-3 approval quorum before signing.",
    conditions: [
      { field: "Source wallet", operator: "in", value: "Treasury group" },
      { field: "Amount (USD equivalent)", operator: ">", value: "$50,000" },
      { field: "Approvals required", operator: "=", value: "2 of 3" },
    ],
  },
  {
    id: "POL-004",
    name: "Screening threshold — Chainalysis risk score",
    category: "Screening",
    status: "active",
    statusLabel: "Active",
    statusTone: "success",
    wallets: 5,
    walletIds: ["WLT-001", "WLT-002", "WLT-003", "WLT-004", "WLT-005"],
    lastEvaluated: "2026-09-15 10:45 UTC",
    type: "Screening",
    created: "2026-03-15",
    modified: "2026-08-04 16:22 UTC",
    description: "Holds any transfer whose counterparty risk score exceeds 0.70 for manual review.",
    conditions: [
      { field: "Risk score", operator: ">", value: "0.70" },
      { field: "Provider", operator: "=", value: "Chainalysis KYT" },
      { field: "Action", operator: "=", value: "Hold for manual review" },
    ],
  },
  {
    id: "POL-005",
    name: "Contract interaction allowlist",
    category: "Contract Allowlist",
    status: "active",
    statusLabel: "Active",
    statusTone: "success",
    wallets: 2,
    walletIds: ["WLT-001", "WLT-005"],
    lastEvaluated: "2026-09-14 22:10 UTC",
    type: "Contract Allowlist",
    created: "2026-07-09",
    modified: "2026-08-25 10:05 UTC",
    description: "Only whitelisted smart contracts may be called from DeFi-enabled wallets.",
    conditions: [
      { field: "Target contract", operator: "not in", value: "Contract allowlist" },
      { field: "Action", operator: "=", value: "Block" },
    ],
  },
  {
    id: "POL-006",
    name: "Automated treasury sweep",
    category: "Velocity",
    status: "draft",
    statusLabel: "Draft",
    statusTone: "neutral",
    wallets: 0,
    walletIds: [],
    lastEvaluated: "—",
    type: "Velocity",
    created: "2026-09-12",
    modified: "2026-09-12 15:48 UTC",
    description: "Sweeps operating balances above a target threshold into the treasury cold wallet nightly.",
    conditions: [
      { field: "Source wallet", operator: "=", value: "HT Operations Hot" },
      { field: "Target balance", operator: "<", value: "$5,000,000" },
      { field: "Schedule", operator: "=", value: "Daily 02:00 UTC" },
    ],
  },
];

export const POLICY_STATS = [
  { value: "6", label: "Total Policies", tone: "default" as const },
  { value: "5", label: "Active", tone: "success" as const },
  { value: "1", label: "Draft", tone: "default" as const },
  { value: "142", label: "Evaluations Today", tone: "default" as const },
];

export const GOVERNANCE_CATEGORIES: { category: PolicyCategory; count: number }[] = [
  { category: "Velocity", count: 2 },
  { category: "Address Allowlist", count: 1 },
  { category: "Quorum", count: 1 },
  { category: "Screening", count: 1 },
  { category: "Contract Allowlist", count: 1 },
];

export interface PolicyApprover {
  initials: string;
  name: string;
  role: string;
  statusLabel: string;
  statusTone: OversightTone;
}

export const POLICY_APPROVAL_ROUTE: { label: string; needed: number; total: number; approvers: PolicyApprover[] } = {
  label: "2-of-3 Approval Route",
  needed: 2,
  total: 3,
  approvers: [
    { initials: "MW", name: "Marcus Webb", role: "Security Admin", statusLabel: "On route", statusTone: "neutral" },
    { initials: "EV", name: "Elena Vasquez", role: "Compliance Officer", statusLabel: "On route", statusTone: "neutral" },
    { initials: "HL", name: "Hannah Lindqvist", role: "Treasury Lead", statusLabel: "On route", statusTone: "neutral" },
  ],
};

export interface Evaluation {
  id: string;
  policyId: string;
  policyName: string;
  intent: string;
  intentHref?: string;
  result: "Passed" | "Blocked" | "Held" | "Requires Approval";
  resultTone: OversightTone;
  actor: string;
  timestamp: string;
  evidenceId?: string;
}

/* intentHref deliberately has NO ?state= query suffix: the linked transfer's
   detail page resolves its displayed state from the live transfer store
   (stores/transfer-store.ts), which is mutable via the Approval flow. A
   hardcoded state param would override that and could show a stale snapshot
   (e.g. TXI-003 was "pending-approval" when this fixture was authored, but
   is "approved" after going through the Approval flow) instead of the
   transfer's real current state. */
export const POLICY_EVALUATIONS: Evaluation[] = [
  { id: "EVL-0142", policyId: "POL-003", policyName: "Quorum approval for treasury movements", intent: "TXI-003 — 100.00 ETH", intentHref: "/transfers/TXI-003", result: "Requires Approval", resultTone: "warning", actor: "PolicyKit v2.1.0", timestamp: "2026-09-15 10:42:02", evidenceId: "EVD-002" },
  { id: "EVL-0141", policyId: "POL-001", policyName: "High-value transfer velocity limit", intent: "TXI-004 — 75,000 USDT", intentHref: "/transfers/TXI-004", result: "Passed", resultTone: "success", actor: "PolicyKit v2.1.0", timestamp: "2026-09-15 10:40:01" },
  { id: "EVL-0140", policyId: "POL-004", policyName: "Screening threshold — Chainalysis risk score", intent: "TXI-004 — address 0xc3d…7e2f", result: "Held", resultTone: "warning", actor: "RiskGuard v1.8.2", timestamp: "2026-09-15 10:40:01", evidenceId: "EVD-008" },
  { id: "EVL-0139", policyId: "POL-005", policyName: "Contract interaction allowlist", intent: "STK-001 — staking contract call", result: "Passed", resultTone: "success", actor: "PolicyKit v2.1.0", timestamp: "2026-09-15 10:36:44" },
  { id: "EVL-0138", policyId: "POL-001", policyName: "High-value transfer velocity limit", intent: "TXI-001 — 50.00 ETH", intentHref: "/transfers/TXI-001", result: "Passed", resultTone: "success", actor: "PolicyKit v2.1.0", timestamp: "2026-09-15 10:12:02", evidenceId: "EVD-001" },
  { id: "EVL-0137", policyId: "POL-002", policyName: "Approved recipient list — external addresses", intent: "TXI-007 — 10.00 ETH", intentHref: "/transfers/TXI-007", result: "Blocked", resultTone: "danger", actor: "PolicyKit v2.1.0", timestamp: "2026-09-15 09:25:02", evidenceId: "EVD-005" },
  { id: "EVL-0136", policyId: "POL-004", policyName: "Screening threshold — Chainalysis risk score", intent: "TXI-006 — address 0x9e…4b1c", result: "Passed", resultTone: "success", actor: "RiskGuard v1.8.2", timestamp: "2026-09-15 09:50:02" },
  { id: "EVL-0135", policyId: "POL-003", policyName: "Quorum approval for treasury movements", intent: "TXI-005 — 2.5000 BTC", intentHref: "/transfers/TXI-005", result: "Requires Approval", resultTone: "warning", actor: "PolicyKit v2.1.0", timestamp: "2026-09-15 09:36:02" },
  { id: "EVL-0134", policyId: "POL-001", policyName: "High-value transfer velocity limit", intent: "TXI-006 — 50,000 MATIC", intentHref: "/transfers/TXI-006", result: "Passed", resultTone: "success", actor: "PolicyKit v2.1.0", timestamp: "2026-09-15 09:50:02" },
  { id: "EVL-0133", policyId: "POL-005", policyName: "Contract interaction allowlist", intent: "Lending deposit — Aave v3", result: "Blocked", resultTone: "danger", actor: "PolicyKit v2.1.0", timestamp: "2026-09-14 22:10:11" },
];

export const GOV_EVAL_STATS = [
  { value: "142", label: "Evaluations Today", tone: "default" as const },
  { value: "138", label: "Passed", tone: "success" as const },
  { value: "3", label: "Held / Requires Approval", tone: "warning" as const },
  { value: "1", label: "Blocked", tone: "danger" as const },
];

/* ───────────────────────── Evidence (EVD-00..03) ───────────────────────── */

export type EvidenceType = "Transfer" | "Approval" | "Recovery" | "Admin" | "Treasury" | "Screening";
export type EvidenceStatus = "complete" | "incomplete";
export type EvidenceTone = "success" | "warning";

export interface EvidencePacket {
  id: string;
  type: EvidenceType;
  subject: string;
  created: string;
  actor: string;
  status: EvidenceStatus;
  statusLabel: string;
  statusTone: EvidenceTone;
  linkLabel: string;
  linkHref: string;
}

export const EVIDENCE_PACKETS: EvidencePacket[] = [
  { id: "EVD-001", type: "Transfer", subject: "TXI-001 — 150 ETH sweep", created: "2026-09-15 09:12", actor: "Sai Suman Kanuganti", status: "complete", statusLabel: "Complete", statusTone: "success", linkLabel: "View transfer", linkHref: "/transfers/TXI-001?state=reconciled" },
  { id: "EVD-002", type: "Transfer", subject: "TXI-003 — 75,000 USDC", created: "2026-09-15 09:45", actor: "Sai Suman Kanuganti", status: "complete", statusLabel: "Complete", statusTone: "success", linkLabel: "View transfer", linkHref: "/transfers/TXI-003?state=pending-approval" },
  { id: "EVD-003", type: "Approval", subject: "APR-001 — Dual approval for TXI-003", created: "2026-09-15 10:02", actor: "Marcus Webb", status: "complete", statusLabel: "Complete", statusTone: "success", linkLabel: "View approval", linkHref: "/approvals/APR-001" },
  { id: "EVD-004", type: "Recovery", subject: "Key rotation — WLT-002", created: "2026-09-14 16:30", actor: "Marcus Webb", status: "complete", statusLabel: "Complete", statusTone: "success", linkLabel: "View wallet", linkHref: "/wallets/WLT-002" },
  { id: "EVD-005", type: "Admin", subject: "User suspension — USR-010", created: "2026-09-14 11:00", actor: "Hannah Lindqvist", status: "complete", statusLabel: "Complete", statusTone: "success", linkLabel: "View user", linkHref: "/admin/users/USR-010" },
  { id: "EVD-006", type: "Treasury", subject: "Staking position STK-001", created: "2026-09-13 14:20", actor: "Sai Suman Kanuganti", status: "complete", statusLabel: "Complete", statusTone: "success", linkLabel: "View position", linkHref: "/treasury" },
  { id: "EVD-007", type: "Transfer", subject: "TXI-007 — Failed broadcast", created: "2026-09-15 10:30", actor: "System", status: "incomplete", statusLabel: "Incomplete", statusTone: "warning", linkLabel: "View transfer", linkHref: "/transfers/TXI-007" },
  { id: "EVD-008", type: "Screening", subject: "Address screening — TXI-003", created: "2026-09-15 09:46", actor: "System (Chainalysis)", status: "complete", statusLabel: "Complete", statusTone: "success", linkLabel: "View policy", linkHref: "/governance/POL-004" },
];

export const EVIDENCE_STATS = [
  { value: "8", label: "Total Packets" },
  { value: "4", label: "Transfer" },
  { value: "1", label: "Approval" },
  { value: "1", label: "Recovery" },
  { value: "1", label: "Admin" },
  { value: "1", label: "Screening" },
];

export const EVIDENCE_TYPE_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All Types" },
  { value: "Transfer", label: "Transfer" },
  { value: "Approval", label: "Approval" },
  { value: "Recovery", label: "Recovery" },
  { value: "Admin", label: "Admin" },
  { value: "Treasury", label: "Treasury" },
  { value: "Screening", label: "Screening" },
];

export const EVIDENCE_ACTOR_FILTERS = ["All Actors", "Sai Suman Kanuganti", "Marcus Webb", "Hannah Lindqvist", "System"];

export interface EvidenceEvent {
  ts: string;
  title: string;
  detail: string;
  tone: OversightTone;
  href?: string;
  hash?: string;
}

export const EVIDENCE_TIMELINE: {
  id: string;
  transferRef: string;
  amount: string;
  route: string;
  chain: string;
  statusLabel: string;
  description: string;
  events: EvidenceEvent[];
} = {
  id: "EVD-002",
  transferRef: "TXF-2024-0891",
  amount: "125 ETH · ≈ $400,250",
  route: "WLT-ILL-001 → 0x7f4e…3d2a",
  chain: "Ethereum mainnet",
  statusLabel: "Reconciled",
  description: "Sealed chain-of-custody audit ladder for the compliance packet (illustrative reference transfer intent).",
  events: [
    { ts: "09:00:12", title: "Transfer Created", detail: "Daniel Park (Finance Operator) · WLT-ILL-001", tone: "success", hash: "sha256:1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d" },
    { ts: "09:00:14", title: "Policy Evaluation", detail: "PolicyKit v2.1.0 · rule-set tier-1-institutional · result REQUIRES_APPROVAL", tone: "success", hash: "sha256:2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e" },
    { ts: "09:00:31", title: "Screening Passed", detail: "RiskGuard v1.8.2 · risk_score 0.12 · sanctions_match false", tone: "success", hash: "sha256:3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f" },
    { ts: "09:01:02", title: "Approval Requested", detail: "assigned to Priya Sharma · SLA 4h", tone: "success", hash: "sha256:4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a" },
    { ts: "13:30:18", title: "Approval Granted", detail: "Priya Sharma (Org Approver) · MFA passkey · IP 10.0.4.22", tone: "success", hash: "sha256:5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b" },
    { ts: "13:45:03", title: "Signing Authorized", detail: "VaultCore HSM-01 · Fireblocks MPC · quorum 3-of-5", tone: "success", hash: "sha256:6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c" },
    { ts: "13:46:58", title: "Transaction Broadcast", detail: "Broadcast Gateway · Alchemy node · tx 0x9f3e2a1c…3a2b", tone: "success", hash: "sha256:7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d" },
    { ts: "13:47:22", title: "Blockchain Confirmed", detail: "Ethereum mainnet · block 19,012,847 · 14 confirmations", tone: "success", hash: "sha256:8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e" },
    { ts: "14:22:05", title: "Reconciliation Complete", detail: "ReconFlow v1.2.0 · ledger match confirmed · discrepancy none", tone: "success", hash: "sha256:9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f" },
  ],
};

export const PACKET_TIMELINE: EvidenceEvent[] = [
  { ts: "09:45:12", title: "Transfer intent created", detail: "Sai Suman Kanuganti initiated 75,000 USDC transfer from WLT-003 to 0x7a2B…f4E1", tone: "neutral" },
  { ts: "09:45:14", title: "Policy evaluation — POL-003 triggered", detail: "Dual-approval required (amount > 50,000 USDC).", tone: "neutral", href: "/governance/POL-003" },
  { ts: "09:46:02", title: "Screening passed", detail: "Chainalysis: address clear, risk score 0.02. EVD-008 created.", tone: "success" },
  { ts: "09:47:00", title: "Approval requested — APR-001", detail: "Quorum: 2 of [Marcus Webb, Elena Vasquez, Hannah Lindqvist]", tone: "neutral" },
  { ts: "10:02:18", title: "Approved by Marcus Webb", detail: "1 of 2 required approvals", tone: "success" },
  { ts: "10:05:44", title: "Approved by Elena Vasquez", detail: "2 of 2 — quorum met", tone: "success" },
  { ts: "10:06:01", title: "Signing authorized", detail: "MPC signing ceremony initiated via Fireblocks", tone: "neutral" },
  { ts: "10:07:33", title: "Broadcast", detail: "Tx hash 0x9c3f…b827 submitted to Ethereum mainnet", tone: "neutral" },
  { ts: "10:12:07", title: "Confirmed — Block 18,945,228", detail: "12 confirmations. Gas used 21,000 (0.00042 ETH)", tone: "success" },
  { ts: "10:15:00", title: "Reconciled & sealed", detail: "Balance delta verified. Packet sealed with integrity hash.", tone: "success", href: "/reconciliation" },
];

export const PACKET_META = {
  id: "EVD-002",
  type: "Transfer",
  subject: "TXI-003 — 75,000 USDC (Ethereum)",
  actor: "Sai Suman Kanuganti (USR-003)",
  created: "2026-09-15 09:45 UTC",
  sealed: "2026-09-15 10:15 UTC",
  transferHref: "/transfers/TXI-003?state=pending-approval",
  approvalHref: "/approvals/APR-001",
  receipt: [
    { k: "Policy", v: "POL-003 — High-value transfer dual approval" },
    { k: "Threshold", v: "> 50,000 USDC equivalent" },
    { k: "Result", v: "Passed (quorum 2/2 met)" },
    { k: "Risk Score", v: "0.12 / 1.00 — Low" },
  ],
  screening: [
    { k: "Provider", v: "Chainalysis KYT" },
    { k: "Address", v: "0x7a2B…f4E1" },
  ],
};

export const EXPORT_CONFIG = {
  dateRange: "2026-09-13 00:00 — 2026-09-15 10:45 UTC",
  packetTypes: "All (Transfer, Approval, Recovery, Admin, Treasury, Screening)",
  initiatedBy: "Sofia Torres (USR-009, Auditor)",
  formats: ["JSON", "CSV", "PDF"] as const,
};

export const EXPORT_RECEIPT = {
  id: "EXP-2026-09-15-001",
  generated: "2026-09-15 10:45:32 UTC",
  actor: "Sofia Torres (USR-009, Auditor)",
  packetCount: 8,
  hash: "sha256:c4a9…e102",
};

/* ───────────────────────── Reconciliation (REC-00/01/02/03) ───────────────────────── */

export const RECON_STATS = [
  { value: "142", label: "Matched Transactions", tone: "success" as const },
  { value: "3", label: "Unmatched", tone: "warning" as const },
  { value: "1", label: "Breaks", tone: "danger" as const },
  { value: "47/50", label: "Sources Fresh", tone: "default" as const },
];

export const RECON_CURRENT_RUN = {
  id: "RUN-005",
  started: "2026-09-15 10:30 UTC",
  statusLabel: "Running",
  statusTone: "info" as const,
  processed: 89,
  total: 143,
  percent: 62,
  complete: false,
};

export const RECON_LAST_RUN = {
  id: "RUN-001",
  completed: "2026-09-15 06:14 UTC",
  duration: "14m 22s",
  matched: 142,
  unmatched: 3,
  breaks: 1,
};

export const RECON_SOURCES = [
  { name: "Fireblocks", statusLabel: "Fresh", statusTone: "success" as const, ago: "2 min ago" },
  { name: "Coinbase Prime", statusLabel: "Fresh", statusTone: "success" as const, ago: "5 min ago" },
  { name: "Anchorage Digital", statusLabel: "Fresh", statusTone: "success" as const, ago: "3 min ago" },
  { name: "Bank of New York (Ledger)", statusLabel: "Fresh", statusTone: "success" as const, ago: "8 min ago" },
  { name: "Ethereum Node", statusLabel: "Fresh", statusTone: "success" as const, ago: "1 min ago" },
  { name: "Bitcoin Node", statusLabel: "Fresh", statusTone: "success" as const, ago: "4 min ago" },
];

export type ExceptionType = "Amount mismatch" | "Missing on provider" | "Timing difference" | "Duplicate entry";
export type ExceptionStatus = "Open" | "Investigating" | "Auto-resolved" | "Resolved";

export interface ReconException {
  id: string;
  run: string;
  type: ExceptionType;
  wallet: string;
  walletName: string;
  amount: string;
  status: ExceptionStatus;
  assigned: string;
  asset: string;
  alloyBalance: string;
  providerBalance: string;
  difference: string;
  detected: string;
  investigation: { ts: string; desc: string }[];
  evidenceId?: string;
}

export const EXCEPTIONS: ReconException[] = [
  {
    id: "EXC-001",
    run: "RUN-001",
    type: "Amount mismatch",
    wallet: "WLT-001",
    walletName: "HT Operations Hot",
    amount: "0.003 ETH",
    status: "Open",
    assigned: "Amina Al-Rashid",
    asset: "ETH",
    alloyBalance: "150.003 ETH",
    providerBalance: "150.000 ETH",
    difference: "+0.003 ETH",
    detected: "2026-09-15 06:14 UTC",
    evidenceId: "EVD-001",
    investigation: [
      { ts: "2026-09-15 06:14 UTC", desc: "Exception created by RUN-001. Amount mismatch detected: Alloy 150.003 ETH vs Fireblocks 150.000 ETH." },
      { ts: "2026-09-15 07:02 UTC", desc: "Assigned to Amina Al-Rashid (USR-006)." },
      { ts: "2026-09-15 08:30 UTC", desc: 'Amina: "Checking on-chain transaction log for pending micro-deposit. Likely a fee refund not yet reflected by Fireblocks."' },
    ],
  },
  {
    id: "EXC-002",
    run: "RUN-001",
    type: "Missing on provider",
    wallet: "WLT-004",
    walletName: "HT Polygon Ops",
    amount: "500 USDC",
    status: "Investigating",
    assigned: "Amina Al-Rashid",
    asset: "USDC",
    alloyBalance: "500.000 USDC",
    providerBalance: "—",
    difference: "+500.000 USDC",
    detected: "2026-09-15 06:14 UTC",
    investigation: [
      { ts: "2026-09-15 06:14 UTC", desc: "Exception created by RUN-001. Ledger entry present in Alloy; no matching provider record." },
      { ts: "2026-09-15 07:14 UTC", desc: "Assigned to Amina Al-Rashid (USR-006)." },
    ],
  },
  {
    id: "EXC-003",
    run: "RUN-001",
    type: "Timing difference",
    wallet: "WLT-005",
    walletName: "HT DeFi Hot",
    amount: "10,000 USDC",
    status: "Auto-resolved",
    assigned: "System",
    asset: "USDC",
    alloyBalance: "10,000.000 USDC",
    providerBalance: "10,000.000 USDC",
    difference: "0.000 USDC",
    detected: "2026-09-15 06:14 UTC",
    investigation: [
      { ts: "2026-09-15 06:14 UTC", desc: "Timing difference detected — provider settlement posted 40s after ledger entry." },
      { ts: "2026-09-15 06:22 UTC", desc: "Auto-resolved after provider settlement reconciliation window closed." },
    ],
  },
  {
    id: "EXC-004",
    run: "RUN-003",
    type: "Duplicate entry",
    wallet: "WLT-003",
    walletName: "HT BTC Reserve",
    amount: "0.1 BTC",
    status: "Resolved",
    assigned: "Amina Al-Rashid",
    asset: "BTC",
    alloyBalance: "0.100 BTC",
    providerBalance: "0.100 BTC",
    difference: "0.000 BTC",
    detected: "2026-09-14 22:10 UTC",
    investigation: [
      { ts: "2026-09-14 22:10 UTC", desc: "Duplicate provider webhook produced two ledger entries." },
      { ts: "2026-09-14 23:05 UTC", desc: "Marked resolved by Amina Al-Rashid (USR-006) — duplicate entry removed." },
    ],
  },
];

export const EXCEPTION_TYPE_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All Types" },
  { value: "Amount mismatch", label: "Amount mismatch" },
  { value: "Missing on provider", label: "Missing on provider" },
  { value: "Timing difference", label: "Timing difference" },
  { value: "Duplicate entry", label: "Duplicate entry" },
];

export const EXCEPTION_STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All Statuses" },
  { value: "Open", label: "Open" },
  { value: "Investigating", label: "Investigating" },
  { value: "Auto-resolved", label: "Auto-resolved" },
  { value: "Resolved", label: "Resolved" },
];

export interface LedgerComparisonRow {
  asset: string;
  cells: { label: string; value: string; matched: boolean }[];
}

export interface LedgerComparisonCard {
  id: string;
  name: string;
  meta: string;
  headers: string[];
  rows: LedgerComparisonRow[];
  note: string;
  noteTone: "danger" | "success";
  noteLink?: { label: string; href: string };
}

export const LEDGER_COMPARISON: LedgerComparisonCard[] = [
  {
    id: "WLT-001",
    name: "HT Operations Hot Wallet",
    meta: "Comparison as of 2026-09-15 06:14 UTC (RUN-001) · Provider: Fireblocks",
    headers: ["Asset", "Alloy Ledger", "Fireblocks", "Bank Ledger"],
    rows: [
      {
        asset: "ETH",
        cells: [
          { label: "Alloy Ledger", value: "150.003 ETH", matched: false },
          { label: "Fireblocks", value: "150.000 ETH", matched: false },
          { label: "Bank Ledger", value: "150.003 ETH", matched: true },
        ],
      },
      {
        asset: "USDC",
        cells: [
          { label: "Alloy Ledger", value: "245,000.00", matched: true },
          { label: "Fireblocks", value: "245,000.00", matched: true },
          { label: "Bank Ledger", value: "245,000.00", matched: true },
        ],
      },
      {
        asset: "WBTC",
        cells: [
          { label: "Alloy Ledger", value: "2.5000", matched: true },
          { label: "Fireblocks", value: "2.5000", matched: true },
          { label: "Bank Ledger", value: "2.5000", matched: true },
        ],
      },
    ],
    note: "ETH: 0.003 difference between Alloy and Fireblocks.",
    noteTone: "danger",
    noteLink: { label: "See EXC-001 evidence", href: "/evidence/EVD-001" },
  },
  {
    id: "WLT-002",
    name: "HT Treasury Cold",
    meta: "Comparison as of 2026-09-15 06:14 UTC (RUN-001) · Provider: Anchorage",
    headers: ["Asset", "Alloy Ledger", "Anchorage", "Bank Ledger"],
    rows: [
      {
        asset: "BTC",
        cells: [
          { label: "Alloy Ledger", value: "15.0000", matched: true },
          { label: "Anchorage", value: "15.0000", matched: true },
          { label: "Bank Ledger", value: "15.0000", matched: true },
        ],
      },
      {
        asset: "ETH",
        cells: [
          { label: "Alloy Ledger", value: "500.000", matched: true },
          { label: "Anchorage", value: "500.000", matched: true },
          { label: "Bank Ledger", value: "500.000", matched: true },
        ],
      },
    ],
    note: "All balances matched.",
    noteTone: "success",
  },
];

export type ReconRunStatus = "Running" | "Completed";

export interface ReconRun {
  id: string;
  started: string;
  status: ReconRunStatus;
  matched: number;
  unmatched: number;
  breaks: number;
  duration: string;
}

export const RECON_RUNS: ReconRun[] = [
  { id: "RUN-005", started: "2026-09-15 10:30 UTC", status: "Running", matched: 89, unmatched: 1, breaks: 0, duration: "15m+" },
  { id: "RUN-001", started: "2026-09-15 06:00 UTC", status: "Completed", matched: 142, unmatched: 3, breaks: 1, duration: "14m 22s" },
  { id: "RUN-002", started: "2026-09-14 06:00 UTC", status: "Completed", matched: 138, unmatched: 0, breaks: 0, duration: "12m 08s" },
  { id: "RUN-003", started: "2026-09-13 06:00 UTC", status: "Completed", matched: 135, unmatched: 2, breaks: 2, duration: "13m 45s" },
  { id: "RUN-004", started: "2026-09-12 06:00 UTC", status: "Completed", matched: 130, unmatched: 1, breaks: 0, duration: "11m 57s" },
];

export const RECON_RUN_TREND = [
  { label: "Avg Matched", value: "136.4" },
  { label: "Avg Duration", value: "13m 02s" },
  { label: "Total Breaks", value: "3" },
  { label: "Total Exceptions", value: "4" },
];

/* ───────────────────────── Monitoring (MON-00..05) ───────────────────────── */

export const MONITOR_STATS = [
  { value: "47/50", label: "Source Freshness", hint: "3 sources within tolerance window" },
  { value: "4/5", label: "Chain Health", hint: "Solana Mainnet degraded" },
  { value: "4/5", label: "Provider Health", hint: "Coinbase Prime degraded" },
  { value: "16", label: "Broadcast Pipeline", hint: "12 queued · 3 in-flight · 1 stuck" },
];

/* MON-00 summary bar. Figma MON-00 draws a literal "8 confirmed / 3 queued /
   3 in-flight / 1" bar, but that contradicts its own "Broadcast Pipeline"
   stat card above (16 = 12 queued · 3 in-flight · 1 stuck) and MON-03
   (Confirmed 8 / In-Flight 3 / Queued 1 / Stuck 0 / Failed 0). Per the
   canonical-conflict decision this renders the MON-03-consistent counts. */
export const MONITOR_PIPELINE_BAR = [
  { label: "Confirmed", flex: 8, tone: "success" as const },
  { label: "In-Flight", flex: 3, tone: "info" as const },
  { label: "Queued", flex: 1, tone: "pending" as const },
  { label: "Stuck", flex: 0, tone: "danger" as const },
];

export type AlertSeverity = "Critical" | "High" | "Medium" | "Low" | "Info";
export type AlertStatus = "Open" | "Investigating" | "Auto-resolved" | "Acknowledged" | "Escalated";

export interface MonitoringAlert {
  id: string;
  title: string;
  severity: AlertSeverity;
  owner: string;
  status: AlertStatus;
  created: string;
  action: "Acknowledge" | "View";
}

/* Canonical Figma MON-05 / MON-00 "Active Alerts" register. */
export const ALERTS: MonitoringAlert[] = [
  { id: "ALT-001", title: "Solana source stale >15min", severity: "High", owner: "Marcus Webb", status: "Open", created: "10:30 UTC", action: "Acknowledge" },
  { id: "ALT-002", title: "Coinbase Prime latency spike", severity: "Medium", owner: "Leo Nakamura", status: "Investigating", created: "10:25 UTC", action: "View" },
  { id: "ALT-003", title: "Broadcast stuck: TXI-007", severity: "High", owner: "Marcus Webb", status: "Open", created: "10:15 UTC", action: "Acknowledge" },
  { id: "ALT-004", title: "ETH gas price surge", severity: "Low", owner: "—", status: "Auto-resolved", created: "09:55 UTC", action: "View" },
  { id: "ALT-005", title: "BitGo key ceremony reminder", severity: "Info", owner: "Marcus Webb", status: "Acknowledged", created: "09:00 UTC", action: "View" },
  { id: "ALT-006", title: "Reconciliation break detected", severity: "High", owner: "Amina Al-Rashid", status: "Escalated", created: "10:40 UTC", action: "View" },
];

/* MON-00 "Active Alerts" lists only alerts still needing attention
   (Open / Investigating) — matching the three rows shown in Figma MON-00. */
export const ACTIVE_ALERT_STATUSES: AlertStatus[] = ["Open", "Investigating"];

/* Figma MON-05 renders one static "Alert Detail — ALT-001" panel. */
export const ALERT_DETAIL = {
  id: "ALT-001",
  title: "Solana source stale >15min",
  severity: "High" as AlertSeverity,
  owner: "Marcus Webb (USR-005)",
  status: "Open" as AlertStatus,
  created: "2026-09-15 10:30:00 UTC",
  receipt: "Slack #ops-alerts, Email to Marcus Webb",
  evidence: "EVD-001 — Source freshness log",
  timeline: [
    { ts: "10:30:00", title: "Alert created by system monitor" },
    { ts: "10:30:02", title: "Notification sent to Slack #ops-alerts" },
    { ts: "10:30:05", title: "Email sent to marcus.webb@harbortrust.bank" },
    { ts: "10:32:10", title: "Assigned to Marcus Webb by escalation rule" },
  ],
};

export const ALERT_SEVERITY_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All Severities" },
  { value: "Critical", label: "Critical" },
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
  { value: "Info", label: "Info" },
];
export const ALERT_STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "", label: "All Statuses" },
  { value: "Open", label: "Open" },
  { value: "Investigating", label: "Investigating" },
  { value: "Auto-resolved", label: "Auto-resolved" },
  { value: "Acknowledged", label: "Acknowledged" },
  { value: "Escalated", label: "Escalated" },
];

export interface ChainHealth {
  id: string;
  name: string;
  statusLabel: string;
  statusTone: OversightTone;
  blockHeight: string;
  lastObservation: string;
  finality: string;
  reorgs: string;
  watches: { id: string; address: string; type: string; since: string; lastEvent: string }[];
  checkpoints: { ts: string; desc: string }[];
}

export const CHAINS: ChainHealth[] = [
  {
    id: "eth-mainnet",
    name: "Ethereum Mainnet",
    statusLabel: "Healthy",
    statusTone: "success",
    blockHeight: "18,945,230",
    lastObservation: "2026-09-15 10:44:52 UTC",
    finality: "12 blocks",
    reorgs: "0",
    watches: [
      { id: "WCH-001", address: "0x7a2B…4f1E", type: "Balance", since: "2026-08-01", lastEvent: "10:44:52 UTC" },
      { id: "WCH-002", address: "0x3c9D…8a2F", type: "Contract Event", since: "2026-08-15", lastEvent: "10:42:11 UTC" },
      { id: "WCH-003", address: "0x1bE5...c7D9", type: "Pending TX", since: "2026-09-10", lastEvent: "10:44:30 UTC" },
    ],
    checkpoints: [
      { ts: "10:44:52 UTC", desc: "Block 18,945,230 confirmed, 0 pending reorgs" },
      { ts: "10:40:01 UTC", desc: "Block 18,945,210 finalized" },
      { ts: "10:35:12 UTC", desc: "Block 18,945,190 finalized" },
      { ts: "10:30:00 UTC", desc: "Scheduled checkpoint: all clear" },
    ],
  },
  {
    id: "eth-sepolia",
    name: "Ethereum Sepolia",
    statusLabel: "Healthy / Sandbox",
    statusTone: "info",
    blockHeight: "6,234,112",
    lastObservation: "2026-09-15 10:44:40 UTC",
    finality: "6 blocks",
    reorgs: "0",
    watches: [{ id: "WCH-004", address: "0x5d1A...9b3C", type: "Balance", since: "2026-09-01", lastEvent: "10:44:40 UTC" }],
    checkpoints: [
      { ts: "10:44:40 UTC", desc: "Block 6,234,112 confirmed, sandbox environment" },
      { ts: "10:34:40 UTC", desc: "Block 6,234,082 finalized" },
    ],
  },
  {
    id: "btc-mainnet",
    name: "Bitcoin Mainnet",
    statusLabel: "Healthy",
    statusTone: "success",
    blockHeight: "862,419",
    lastObservation: "2026-09-15 10:43:02 UTC",
    finality: "6 blocks",
    reorgs: "0",
    watches: [{ id: "WCH-005", address: "3FE7bai...9d2c", type: "Balance", since: "2026-07-20", lastEvent: "10:43:02 UTC" }],
    checkpoints: [
      { ts: "10:43:02 UTC", desc: "Block 862,419 confirmed" },
      { ts: "10:31:10 UTC", desc: "Block 862,418 finalized" },
    ],
  },
  {
    id: "polygon-mainnet",
    name: "Polygon Mainnet",
    statusLabel: "Healthy",
    statusTone: "success",
    blockHeight: "51,298,410",
    lastObservation: "2026-09-15 10:44:11 UTC",
    finality: "128 blocks",
    reorgs: "0",
    watches: [{ id: "WCH-006", address: "0x9e...4b1c", type: "Contract Event", since: "2026-08-05", lastEvent: "10:41:55 UTC" }],
    checkpoints: [
      { ts: "10:44:11 UTC", desc: "Block 51,298,410 confirmed" },
      { ts: "10:33:20 UTC", desc: "Block 51,298,280 finalized" },
    ],
  },
  {
    id: "solana-mainnet",
    name: "Solana Mainnet",
    statusLabel: "Degraded",
    statusTone: "warning",
    blockHeight: "245,891,002",
    lastObservation: "2026-09-15 10:32:10 UTC",
    finality: "32 slots",
    reorgs: "1",
    watches: [{ id: "WCH-007", address: "So1a...9xQ", type: "Balance", since: "2026-09-02", lastEvent: "10:30:02 UTC" }],
    checkpoints: [
      { ts: "10:32:10 UTC", desc: "Block 245,891,002 confirmed — source stale beyond tolerance" },
      { ts: "10:21:44 UTC", desc: "Block 245,890,810 finalized" },
    ],
  },
];

export interface ProviderHealth {
  id: string;
  name: string;
  statusLabel: string;
  statusTone: OversightTone;
  uptime: string;
  errorRate: string;
  latencyP50: string;
  latencyP99: string;
  capabilities: { name: string; enabled: boolean }[];
  probes: string[];
}

export const PROVIDERS: ProviderHealth[] = [
  {
    id: "PROV-001",
    name: "Fireblocks",
    statusLabel: "Healthy",
    statusTone: "success",
    uptime: "99.97%",
    errorRate: "0.02%",
    latencyP50: "145 ms",
    latencyP99: "890 ms",
    capabilities: [
      { name: "Custody", enabled: true },
      { name: "Signing", enabled: true },
      { name: "Broadcast", enabled: true },
      { name: "Balance Query", enabled: true },
      { name: "Webhooks", enabled: true },
      { name: "Staking", enabled: false },
    ],
    probes: ["10:44 OK 142ms", "10:39 OK 138ms", "10:34 OK 151ms", "10:29 OK 145ms", "10:24 OK 139ms", "10:19 OK 148ms", "10:14 OK 144ms", "10:09 OK 140ms", "10:04 OK 152ms", "09:59 OK 147ms"],
  },
  {
    id: "PROV-002",
    name: "BitGo",
    statusLabel: "Healthy",
    statusTone: "success",
    uptime: "99.91%",
    errorRate: "0.03%",
    latencyP50: "132 ms",
    latencyP99: "760 ms",
    capabilities: [
      { name: "Custody", enabled: true },
      { name: "Signing", enabled: true },
      { name: "Broadcast", enabled: true },
      { name: "Balance Query", enabled: true },
      { name: "Webhooks", enabled: true },
      { name: "Staking", enabled: true },
    ],
    probes: ["10:44 OK 130ms", "10:39 OK 128ms", "10:34 OK 135ms", "10:29 OK 133ms", "10:24 OK 129ms", "10:19 OK 137ms", "10:14 OK 131ms", "10:09 OK 134ms", "10:04 OK 130ms", "09:59 OK 136ms"],
  },
  {
    id: "PROV-003",
    name: "Mesh/VaultCore",
    statusLabel: "Healthy",
    statusTone: "success",
    uptime: "99.99%",
    errorRate: "0.01%",
    latencyP50: "98 ms",
    latencyP99: "410 ms",
    capabilities: [
      { name: "Custody", enabled: false },
      { name: "Signing", enabled: true },
      { name: "Broadcast", enabled: true },
      { name: "Balance Query", enabled: true },
      { name: "Webhooks", enabled: true },
      { name: "Staking", enabled: false },
    ],
    probes: ["10:44 OK 96ms", "10:39 OK 99ms", "10:34 OK 97ms", "10:29 OK 101ms", "10:24 OK 95ms", "10:19 OK 100ms", "10:14 OK 98ms", "10:09 OK 97ms", "10:04 OK 102ms", "09:59 OK 94ms"],
  },
  {
    id: "PROV-004",
    name: "Coinbase Prime",
    statusLabel: "Degraded",
    statusTone: "warning",
    uptime: "98.62%",
    errorRate: "1.40%",
    latencyP50: "1,240 ms",
    latencyP99: "4,310 ms",
    capabilities: [
      { name: "Custody", enabled: true },
      { name: "Signing", enabled: true },
      { name: "Broadcast", enabled: true },
      { name: "Balance Query", enabled: true },
      { name: "Webhooks", enabled: false },
      { name: "Staking", enabled: true },
    ],
    probes: ["10:44 SLOW 1240ms", "10:39 SLOW 1310ms", "10:34 OK 880ms", "10:29 SLOW 1420ms", "10:24 OK 910ms", "10:19 SLOW 1280ms", "10:14 OK 870ms", "10:09 SLOW 1390ms", "10:04 OK 905ms", "09:59 OK 860ms"],
  },
  {
    id: "PROV-005",
    name: "Circle",
    statusLabel: "Healthy",
    statusTone: "success",
    uptime: "99.95%",
    errorRate: "0.02%",
    latencyP50: "167 ms",
    latencyP99: "820 ms",
    capabilities: [
      { name: "Custody", enabled: false },
      { name: "Signing", enabled: false },
      { name: "Broadcast", enabled: true },
      { name: "Balance Query", enabled: true },
      { name: "Webhooks", enabled: true },
      { name: "Staking", enabled: false },
    ],
    probes: ["10:44 OK 165ms", "10:39 OK 170ms", "10:34 OK 163ms", "10:29 OK 168ms", "10:24 OK 172ms", "10:19 OK 166ms", "10:14 OK 164ms", "10:09 OK 171ms", "10:04 OK 169ms", "09:59 OK 162ms"],
  },
];

export interface BroadcastRow {
  txId: string;
  chain: string;
  statusLabel: "Confirmed" | "In-Flight" | "Queued" | "Stuck" | "Failed";
  statusTone: OversightTone;
  submitted: string;
  gas: string;
  confirmations: string;
}

export const BROADCAST_ROWS: BroadcastRow[] = [
  { txId: "TXI-001", chain: "Ethereum", statusLabel: "Confirmed", statusTone: "success", submitted: "10:20:00", gas: "32", confirmations: "24" },
  { txId: "TXI-002", chain: "Polygon", statusLabel: "Confirmed", statusTone: "success", submitted: "10:22:15", gas: "45", confirmations: "128" },
  { txId: "TXI-003", chain: "Ethereum", statusLabel: "Confirmed", statusTone: "success", submitted: "10:25:30", gas: "34", confirmations: "18" },
  { txId: "TXI-004", chain: "Bitcoin", statusLabel: "Confirmed", statusTone: "success", submitted: "10:28:00", gas: "—", confirmations: "3" },
  { txId: "TXI-005", chain: "Ethereum", statusLabel: "In-Flight", statusTone: "info", submitted: "10:38:20", gas: "38", confirmations: "2" },
  { txId: "TXI-006", chain: "Polygon", statusLabel: "In-Flight", statusTone: "info", submitted: "10:40:05", gas: "50", confirmations: "1" },
  { txId: "TXI-008", chain: "Ethereum", statusLabel: "In-Flight", statusTone: "info", submitted: "10:42:30", gas: "36", confirmations: "0" },
  { txId: "TXI-009", chain: "Polygon", statusLabel: "Queued", statusTone: "pending", submitted: "10:44:50", gas: "est. 48", confirmations: "—" },
];

export const PIPELINE_BAR = [
  { label: "Confirmed", flex: 8, tone: "success" as const },
  { label: "In-Flight", flex: 3, tone: "info" as const },
  { label: "Queued", flex: 1, tone: "pending" as const },
];

/* MON-03 stat cards are literal Figma values and are intentionally NOT
   derived from BROADCAST_ROWS (the table shows 4 confirmed rows, but the
   canonical summary reports 8 confirmed). */
export const BROADCAST_STATS = [
  { value: "1", label: "Queued" },
  { value: "3", label: "In-Flight" },
  { value: "0", label: "Stuck" },
  { value: "0", label: "Failed" },
  { value: "8", label: "Confirmed" },
];

/* Figma MON-04 "Freshness Matrix — Wallet × Source" — every source is within
   tolerance (the banner is informational, not a warning). */
export const FRESHNESS_ROWS = [
  { wallet: "WLT-001 (ETH Ops)", balance: "2m ago", tx: "1m ago", event: "3m ago", stale: false },
  { wallet: "WLT-003 (BTC Cold)", balance: "4m ago", tx: "4m ago", event: "5m ago", stale: false },
  { wallet: "WLT-005 (Polygon)", balance: "1m ago", tx: "1m ago", event: "2m ago", stale: false },
  { wallet: "WLT-007 (Solana)", balance: "3m ago", tx: "2m ago", event: "4m ago", stale: false },
  { wallet: "WLT-009 (ETH DeFi)", balance: "2m ago", tx: "1m ago", event: "2m ago", stale: false },
];
